/**
 * QuickWinQuadrant — 2×2 matrix plotting every opportunity by P2P score (Y) and
 * source-validation count (X). The top-right cell ("⚡ Quick Wins") highlights
 * the must-build picks: ≥70 score AND ≥2 distinct sources.
 *
 * Pure-SVG, no chart lib. Click any dot → opens the opp via parent's
 * `onSelectOpp` callback (same modal used by OpportunityCard rows).
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const W = 640, H = 380;
const PAD = { l: 56, r: 24, t: 24, b: 48 };
const PLOT_W = W - PAD.l - PAD.r;
const PLOT_H = H - PAD.t - PAD.b;
const MAX_SRC = 5;          // x-axis: 0..5
const SCORE_THRESHOLD = 70; // y-axis "high score" divider
const SOURCE_THRESHOLD = 2; // x-axis "validated" divider

const xPos = (sources) => PAD.l + (Math.min(sources, MAX_SRC) / MAX_SRC) * PLOT_W;
const yPos = (score)   => PAD.t + (1 - Math.max(0, Math.min(100, score)) / 100) * PLOT_H;

const QuickWinQuadrant = ({ onSelectOpp }) => {
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hover, setHover] = useState(null); // { opp, x, y }

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/opportunities?limit=500`, { timeout: 12000 })
      .then((r) => { if (!cancelled) setOpps(r.data || []); })
      .catch(() => { if (!cancelled) setOpps([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Pre-compute dot positions + trajectory line from 7-day prior position.
  const points = useMemo(() => {
    return opps
      .filter((o) => typeof o.weighted_total === "number")
      .map((o) => {
        const sources = o.source_count || 0;
        const score = o.weighted_total || 0;
        const prevSources = (o.prev_source_count ?? sources);
        const prevScore = (o.prev_score ?? score);
        const quickWin = score >= SCORE_THRESHOLD && sources >= SOURCE_THRESHOLD;
        const promising = score >= SCORE_THRESHOLD && sources < SOURCE_THRESHOLD;
        const moved = (prevScore !== score) || (prevSources !== sources);
        return {
          opp: o,
          x: xPos(sources),
          y: yPos(score),
          prevX: xPos(prevSources),
          prevY: yPos(prevScore),
          moved,
          rising: !!o.is_rising,
          quickWin,
          promising,
          score,
          sources,
        };
      });
  }, [opps]);

  const quickWinCount = points.filter((p) => p.quickWin).length;
  const promisingCount = points.filter((p) => p.promising).length;
  const risingCount = points.filter((p) => p.rising).length;
  const movedCount = points.filter((p) => p.moved).length;

  // Axis ticks
  const yTicks = [0, 25, 50, 70, 85, 100];
  const xTicks = [0, 1, 2, 3, 4, 5];

  return (
    <section
      className="bg-white border border-zinc-200 rounded-2xl overflow-hidden"
      data-testid="quick-win-quadrant"
    >
      <header className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-semibold tracking-tight text-zinc-900 flex items-center gap-2">
            <span className="text-emerald-500">⚡</span>
            Quick Win Quadrant
          </h3>
          <p className="text-xs text-zinc-500 mt-0.5">
            High score (P2P ≥70) × Multi-source (≥2 platforms) → top-right = absolute must-build.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap text-[11px] font-mono-data uppercase tracking-[0.15em]">
          <span className="px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
            ⚡ {quickWinCount} quick wins
          </span>
          <span className="px-2.5 py-1 rounded-full bg-violet-50 text-violet-700 border border-violet-200">
            ★ {promisingCount} promising
          </span>
          {risingCount > 0 && (
            <span className="px-2.5 py-1 rounded-full bg-rose-50 text-rose-700 border border-rose-200" data-testid="quick-win-rising-chip">
              🚀 {risingCount} rising
            </span>
          )}
          {movedCount > 0 && (
            <span className="px-2.5 py-1 rounded-full bg-zinc-50 text-zinc-600 border border-zinc-200">
              ↗ {movedCount} moved 7d
            </span>
          )}
        </div>
      </header>

      {loading ? (
        <div className="p-8 text-xs text-zinc-400 uppercase tracking-[0.2em] animate-pulse">
          Loading quadrant…
        </div>
      ) : points.length === 0 ? (
        <div className="p-8 text-sm text-zinc-500">No scored opportunities yet — run an AI Analyze first.</div>
      ) : (
        <div className="relative p-3">
          <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" data-testid="quick-win-svg">
            <defs>
              <marker id="qw-arrow" viewBox="0 0 10 10" refX="8" refY="5"
                      markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#A1A1AA" />
              </marker>
              <marker id="qw-arrow-rising" viewBox="0 0 10 10" refX="8" refY="5"
                      markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#E11D48" />
              </marker>
            </defs>
            {/* Quadrant backgrounds */}
            <rect x={xPos(SOURCE_THRESHOLD)} y={PAD.t}
                  width={W - PAD.r - xPos(SOURCE_THRESHOLD)} height={yPos(SCORE_THRESHOLD) - PAD.t}
                  fill="#ECFDF5" />
            <rect x={PAD.l} y={PAD.t}
                  width={xPos(SOURCE_THRESHOLD) - PAD.l} height={yPos(SCORE_THRESHOLD) - PAD.t}
                  fill="#F5F3FF" />
            <rect x={xPos(SOURCE_THRESHOLD)} y={yPos(SCORE_THRESHOLD)}
                  width={W - PAD.r - xPos(SOURCE_THRESHOLD)} height={H - PAD.b - yPos(SCORE_THRESHOLD)}
                  fill="#F8FAFC" />
            <rect x={PAD.l} y={yPos(SCORE_THRESHOLD)}
                  width={xPos(SOURCE_THRESHOLD) - PAD.l} height={H - PAD.b - yPos(SCORE_THRESHOLD)}
                  fill="#FAFAFA" />

            {/* Quadrant labels */}
            <text x={W - PAD.r - 8} y={PAD.t + 14} textAnchor="end"
                  fontSize="10" fontWeight="700" fill="#047857" letterSpacing="1.5">
              ⚡ QUICK WINS
            </text>
            <text x={PAD.l + 8} y={PAD.t + 14} fontSize="10" fontWeight="700" fill="#6D28D9" letterSpacing="1.5">
              ★ PROMISING (NEEDS VALIDATION)
            </text>
            <text x={W - PAD.r - 8} y={H - PAD.b - 8} textAnchor="end"
                  fontSize="9" fontWeight="500" fill="#71717A" letterSpacing="1">
              VALIDATED · LOW SCORE
            </text>
            <text x={PAD.l + 8} y={H - PAD.b - 8} fontSize="9" fontWeight="500" fill="#71717A" letterSpacing="1">
              UNTRIAGED
            </text>

            {/* Divider lines */}
            <line x1={xPos(SOURCE_THRESHOLD)} y1={PAD.t} x2={xPos(SOURCE_THRESHOLD)} y2={H - PAD.b}
                  stroke="#A1A1AA" strokeWidth="1" strokeDasharray="4 4" />
            <line x1={PAD.l} y1={yPos(SCORE_THRESHOLD)} x2={W - PAD.r} y2={yPos(SCORE_THRESHOLD)}
                  stroke="#A1A1AA" strokeWidth="1" strokeDasharray="4 4" />

            {/* Axes */}
            <line x1={PAD.l} y1={PAD.t} x2={PAD.l} y2={H - PAD.b} stroke="#52525B" strokeWidth="1" />
            <line x1={PAD.l} y1={H - PAD.b} x2={W - PAD.r} y2={H - PAD.b} stroke="#52525B" strokeWidth="1" />

            {/* Y ticks + labels */}
            {yTicks.map((t) => (
              <g key={`yt-${t}`}>
                <line x1={PAD.l - 4} y1={yPos(t)} x2={PAD.l} y2={yPos(t)} stroke="#52525B" />
                <text x={PAD.l - 8} y={yPos(t) + 3} textAnchor="end" fontSize="10" fill="#52525B">{t}</text>
              </g>
            ))}
            {/* X ticks + labels */}
            {xTicks.map((t) => (
              <g key={`xt-${t}`}>
                <line x1={xPos(t)} y1={H - PAD.b} x2={xPos(t)} y2={H - PAD.b + 4} stroke="#52525B" />
                <text x={xPos(t)} y={H - PAD.b + 16} textAnchor="middle" fontSize="10" fill="#52525B">{t}</text>
              </g>
            ))}

            {/* Axis titles */}
            <text x={PAD.l - 42} y={PAD.t + PLOT_H / 2} fontSize="10" fontWeight="600"
                  fill="#3F3F46" textAnchor="middle"
                  transform={`rotate(-90, ${PAD.l - 42}, ${PAD.t + PLOT_H / 2})`}
                  letterSpacing="1.2">
              P2P SCORE →
            </text>
            <text x={PAD.l + PLOT_W / 2} y={H - 6} textAnchor="middle"
                  fontSize="10" fontWeight="600" fill="#3F3F46" letterSpacing="1.2">
              DISTINCT SOURCES →
            </text>

            {/* Trajectory arrows: faint line from 7-day prior position → current.
                 Rising opps (crossed into Quick Wins this week) get rose color. */}
            {points.filter((p) => p.moved).map((p, i) => (
              <line
                key={`traj-${p.opp.id}-${i}`}
                x1={p.prevX}
                y1={p.prevY}
                x2={p.x}
                y2={p.y}
                stroke={p.rising ? "#E11D48" : "#A1A1AA"}
                strokeWidth={p.rising ? 2 : 1}
                strokeOpacity={p.rising ? 0.85 : 0.4}
                markerEnd={p.rising ? "url(#qw-arrow-rising)" : "url(#qw-arrow)"}
                data-testid={`quick-win-traj-${p.opp.id}`}
              />
            ))}

            {/* Data points */}
            {points.map((p, i) => {
              const fill = p.quickWin ? "#059669" : p.promising ? "#7C3AED" : "#A1A1AA";
              const r = p.quickWin ? 6 : 4;
              return (
                <circle
                  key={`pt-${p.opp.id}-${i}`}
                  cx={p.x}
                  cy={p.y}
                  r={r}
                  fill={fill}
                  fillOpacity={p.quickWin ? 0.85 : 0.55}
                  stroke="white"
                  strokeWidth="1.2"
                  style={{ cursor: "pointer" }}
                  onMouseEnter={() => setHover(p)}
                  onMouseLeave={() => setHover(null)}
                  onClick={() => onSelectOpp && onSelectOpp(p.opp)}
                  data-testid={`quick-win-dot-${p.opp.id}`}
                />
              );
            })}
          </svg>

          {/* Hover tooltip */}
          {hover && (
            <div
              className="absolute bg-zinc-900 text-white text-[11px] px-3 py-2 rounded-lg shadow-xl pointer-events-none max-w-xs leading-snug"
              style={{
                left: `min(calc(${(hover.x / W) * 100}% + 12px), calc(100% - 280px))`,
                top: `calc(${(hover.y / H) * 100}% + 12px)`,
              }}
              data-testid="quick-win-tooltip"
            >
              <div className="font-mono-data text-[9px] uppercase tracking-[0.15em] text-emerald-300 mb-1">
                P2P {Math.round(hover.score)} · {hover.sources} source{hover.sources === 1 ? "" : "s"}
              </div>
              <div className="line-clamp-3">{hover.opp.problem}</div>
              <div className="mt-1 text-[9px] text-zinc-400">click to open</div>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default QuickWinQuadrant;
